import { useTranslation } from 'next-i18next'
import React, { useEffect } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { Text, View } from 'react-native'
import { tv } from 'tailwind-variants'

import { useAnalytics } from '@/context/AnalyticsContext'
import { useLocaleParts } from '@/hooks/useLocaleParts'
import { Checkbox } from '@/shared/components/flows/Checkbox'
import { EVENTS } from '@flows/configs/analytics/constants'
import PhosphorIcon from '@ui-kit/components/PhosphorIcon'
import { body1, body2 } from '@ui-kit/core/typography'

const consentStyles = tv({
  slots: {
    card: 'flex-col gap-5 rounded-xl bg-grey-100 p-5',
    intro: body2({ color: 'grey700' }),
    itemTitle: body1({ bold: true, className: 'mb-2' }),
    itemBody: body2(),
    itemInfo: 'mt-3 flex-row gap-2',
    itemInfoText: body2({ color: 'grey700', className: 'flex-1' }),
  },
})

type ConsentItem = {
  key: string
  titleKey: string
  bodyKey: string
  infoKey?: string
}

const IMMEDIATE_COVERAGE_CONSENTS: readonly ConsentItem[] = [
  {
    key: 'immediateCoverageStartConsent',
    titleKey: 'sharedFlows_common:steps.immediateCoverageConsent.startConsent.heading',
    bodyKey: 'sharedFlows_common:steps.immediateCoverageConsent.startConsent.body',
  },
  {
    key: 'immediateCoverageWithdrawalAcknowledgement',
    titleKey: 'sharedFlows_common:steps.immediateCoverageConsent.withdrawalAcknowledgement.heading',
    bodyKey: 'sharedFlows_common:steps.immediateCoverageConsent.withdrawalAcknowledgement.body',
  },
]

const NEW_DESIGN_CONSENTS: readonly ConsentItem[] = [
  {
    key: 'immediateCoverageStartConsent',
    titleKey: 'sharedFlows_common:steps.immediateCoverageConsent.startConsent.heading',
    bodyKey: 'sharedFlows_common:steps.immediateCoverageConsent.startConsent.newDesignBody',
    infoKey: 'sharedFlows_common:steps.immediateCoverageConsent.startConsent.info',
  },
]

interface Props {
  flowRouter: { getImmediateCoverageConsent: { useQuery: any } }
  showValidation?: boolean
  /** One consent with an info line instead of the intro and the withdrawal acknowledgement. */
  newDesign?: boolean
}

export const ImmediateCoverageConsent = (props: Props) => {
  const { t } = useTranslation()
  const { language, market } = useLocaleParts()
  const { track } = useAnalytics()
  const { setValue } = useFormContext()
  const styles = consentStyles()
  const consents = props.newDesign ? NEW_DESIGN_CONSENTS : IMMEDIATE_COVERAGE_CONSENTS

  const consentQuery = props.flowRouter.getImmediateCoverageConsent.useQuery(
    { language, market },
    { refetchOnMount: 'always' }
  )
  const consent = consentQuery.data
  const isRequired = consent?.isRequired === true

  const checkedValues = useWatch({
    name: consents.map((item) => item.key),
  }) as unknown[]
  const items = consents.map((item, index) => ({
    ...item,
    checked: checkedValues[index] === true,
  }))
  const missing = isRequired && !items.every((item) => item.checked)

  useEffect(() => {
    setValue('immediateCoverageConsentMissing', missing)
  }, [missing, setValue])

  if (!isRequired || !consent) {
    return null
  }

  return (
    <View className={styles.card()}>
      {props.newDesign ? null : (
        <Text className={styles.intro()}>
          {t('sharedFlows_common:steps.immediateCoverageConsent.intro', {
            days: consent.withdrawalPeriodDays,
          })}
        </Text>
      )}
      {items.map((item) => (
        <View key={item.key}>
          <Text className={styles.itemTitle()}>{`${t(item.titleKey)} *`}</Text>
          <Checkbox
            testID={item.key}
            checked={item.checked}
            hasError={props.showValidation && !item.checked}
            onPress={() => {
              const next = !item.checked
              setValue(item.key, next, { shouldValidate: true })
              track(EVENTS.LegalConfirmationsCheckboxToggled, {
                confirmationTitle: t(item.titleKey),
                checkboxToggle: next,
              })
            }}
          >
            <Text className={styles.itemBody()}>
              {t(item.bodyKey, { days: consent.withdrawalPeriodDays })}
            </Text>
          </Checkbox>
          {item.infoKey ? (
            <View className={styles.itemInfo()}>
              <PhosphorIcon name="Info" size={20} />
              <Text className={styles.itemInfoText()}>{t(item.infoKey)}</Text>
            </View>
          ) : null}
        </View>
      ))}
    </View>
  )
}
